import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Card, Table, Input, Select, Button, Tag, Typography, Space,
    Row, Col, Divider, Tooltip, Modal, Form, Switch, message, Popconfirm,
    Upload, Alert, Spin, Descriptions,
} from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageFilter from '../components/page/PageFilter';
import DataCard from '../components/page/DataCard';
import {
    ArrowLeftOutlined, ArrowRightOutlined, LockOutlined,
    SafetyCertificateOutlined, GlobalOutlined,
    CreditCardOutlined,
    FileProtectOutlined, DeploymentUnitOutlined, BankOutlined, RobotOutlined,
    CheckCircleOutlined, ExclamationCircleOutlined, EditOutlined, DeleteOutlined,
    UploadOutlined, InboxOutlined, DownloadOutlined, HistoryOutlined,
} from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { useApp } from '../context/AppContext';
import { Role } from '../constants/roles';
import { useResizableTableColumns } from '../hooks/useResizableTableColumns';
import { DASHBOARD_SORTER_TOOLTIP, dashboardPagination } from '../utils/adminDashboardTable';
import { naturalCompare, sortAnnexControlNumber, sortByField, sortKeyPointsCount } from '../utils/tableSorters';
import { formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";
import '../styles/policy-hub-iso.css';
const { Title, Text } = Typography;
const { Dragger } = Upload;

/** Normalize key point text so list markers aren’t duplicated with leading "- " or "•". */
function formatKeyPointLine(raw) {
    if (raw == null) return '';
    const s = String(raw).trim();
    return s.replace(/^[\u2022\u2023\u2043\ufe63\ufe6e•\-\*]\s*/, '').trim();
}

const FAMILY_COLORS = { Organizational: 'blue', People: 'green', Physical: 'orange', Technological: 'purple' };

const FRAMEWORK_ICONS = {
    iso27001: <SafetyCertificateOutlined style={{ fontSize: 32, color: 'var(--color-accent)' }} />,
    pciDss: <CreditCardOutlined style={{ fontSize: 32, color: '#f59e0b' }} />,
    iso27701: <FileProtectOutlined style={{ fontSize: 32, color: '#6366f1' }} />,
    gdpr: <GlobalOutlined style={{ fontSize: 32, color: '#10b981' }} />,
    nis2: <DeploymentUnitOutlined style={{ fontSize: 32, color: '#0d9488' }} />,
    nistCsf: <BankOutlined style={{ fontSize: 32, color: '#7c3aed' }} />,
    fedramp: <BankOutlined style={{ fontSize: 32, color: '#eb2f96' }} />,
    iso42001: <RobotOutlined style={{ fontSize: 32, color: '#db2777' }} />,
    iso22301: <SafetyCertificateOutlined style={{ fontSize: 32, color: '#faad14' }} />,
    dpdpa: <FileProtectOutlined style={{ fontSize: 32, color: '#ea580c' }} />,
    soc2: <SafetyCertificateOutlined style={{ fontSize: 32, color: '#0ea5e9' }} />,
};

/** Completion % = (U ÷ T) × 100 — higher mapped coverage is better. */
function completionPercentTagColor(pct) {
    if (pct == null || Number.isNaN(pct)) return 'default';
    if (pct >= 70) return 'green';
    if (pct >= 40) return 'blue';
    if (pct >= 15) return 'orange';
    return 'red';
}

function formatPolicyHubPercent(p) {
    if (p == null || Number.isNaN(p)) return '-';
    const n = Math.round(Number(p) * 1000) / 1000;
    return Number.isInteger(n) ? `${n}%` : `${n.toFixed(3)}%`;
}

const API_BASE = apiConfig.baseUrl;

/**
 * Policy Library / Policy Hub view.
 * When the user clicks the ISO 27001:2022 card, fetches all controls from the API.
 */
const PolicyLibrary = () => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const instanceRef = useRef(instance);
    const accountRef = useRef(activeAccount);
    useEffect(() => { instanceRef.current = instance; }, [instance]);
    useEffect(() => { accountRef.current = activeAccount; }, [activeAccount]);

    const [activeFramework, setActiveFramework] = useState(null);
    const [controls, setControls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [themeFilter, setThemeFilter] = useState('All');
    const [orgFilter, setOrgFilter] = useState('All');
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [controlModalOpen, setControlModalOpen] = useState(false);
    const [editingControl, setEditingControl] = useState(null);
    const [savingControl, setSavingControl] = useState(false);
    const [controlForm] = Form.useForm();

    /** ISO Policy Hub library versions (1, 1.1, …) — same model as Question Bank */
    const [isoVersions, setIsoVersions] = useState([]);
    const [selectedIsoVersionId, setSelectedIsoVersionId] = useState(null);
    const [loadingIsoVersions, setLoadingIsoVersions] = useState(false);
    const [isoUploadOpen, setIsoUploadOpen] = useState(false);
    const [isoUploading, setIsoUploading] = useState(false);
    const [isoUploadResult, setIsoUploadResult] = useState(null);
    const [isoDownloadLoading, setIsoDownloadLoading] = useState(false);
    const [isoDeleteLoading, setIsoDeleteLoading] = useState(false);

    /** Generic Framework library versions */
    const [genericVersions, setGenericVersions] = useState([]);
    const [selectedGenericVersionId, setSelectedGenericVersionId] = useState(null);
    const [loadingGenericVersions, setLoadingGenericVersions] = useState(false);
    const [genericUploadOpen, setGenericUploadOpen] = useState(false);
    const [genericUploading, setGenericUploading] = useState(false);
    const [genericUploadResult, setGenericUploadResult] = useState(null);
    const [genericDeleteLoading, setGenericDeleteLoading] = useState(false);

    const { user, fetchTaxonomyPendingCount } = useApp();
    const isAdmin = user?.role === Role.ADMIN;

    const [coverage, setCoverage] = useState(null);
    const [coverageLoading, setCoverageLoading] = useState(true);

    // ── Token acquisition (stable refs — matches QuestionBank pattern) ────
    const getToken = async () => {
        try {
            const tokenResponse = await instanceRef.current.acquireTokenSilent({
                ...apiTokenRequest,
                account: accountRef.current,
            });
            return tokenResponse.accessToken;
        } catch {
            return null;
        }
    };

    const fetchCoverageStats = async () => {
        setCoverageLoading(true);
        try {
            const token = await getToken();
            if (!token) {
                setCoverage(null);
                return;
            }
            const res = await fetch(`${API_BASE}/api/v1/policy-hub/coverage`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error?.message || `HTTP ${res.status}`);
            setCoverage(json.data.coverage || null);
        } catch (err) {
            console.error('Policy hub coverage:', err);
            setCoverage(null);
        } finally {
            setCoverageLoading(false);
        }
    };

    useEffect(() => {
        fetchCoverageStats();
    }, []);

    const frameworks = [
        { id: 'iso27001', name: 'ISO 27001:2022', desc: 'Information security management systems - comprehensive controls for protecting organizational information assets.' },
        { id: 'pciDss', name: 'PCI DSS (Version 4.0.1)', desc: 'Payment Card Industry Data Security Standard - secure handling of cardholder data.' },
        { id: 'iso27701', name: 'ISO/IEC 27701:2025 (PIMS)', desc: 'Privacy information management - extends ISO/IEC 27001 for privacy.' },
        { id: 'gdpr', name: 'GDPR (General Data Protection Regulation)', desc: 'EU regulation on data protection and privacy - cross-mapped from Annex A where applicable.' },
        { id: 'nis2', name: 'NIS2.0 (Network and Information Systems Directive)', desc: 'EU cybersecurity risk-management measures for essential and important entities.' },
        { id: 'nistCsf', name: 'NIST CSF', desc: 'NIST Cybersecurity Framework.' },
        { id: 'fedramp', name: 'FedRAMP (Revision 5)', desc: 'Security and privacy controls for federal systems and FedRAMP authorization baselines.' },
        { id: 'iso42001', name: 'ISO/IEC 42001:2023 (AIMS)', desc: 'Artificial intelligence management system - governance of AI systems.' },
        { id: 'iso22301', name: 'ISO 22301', desc: 'Business continuity management systems.' },
        { id: 'dpdpa', name: 'DPDP Act, 2023 (India)', desc: 'Digital Personal Data Protection Act, 2023 and Rules - obligations of data fiduciaries and rights of data principals.' },
        { id: 'soc2', name: 'SOC 2', desc: 'Trust Services Criteria: security, availability, processing integrity, confidentiality, privacy.' },
    ];

    const fetchGenericVersions = async (fwId) => {
        try {
            setLoadingGenericVersions(true);
            const token = await getToken();
            if (!token) return;
            const res = await fetch(`${API_BASE}/api/v1/frameworks/${fwId}/versions`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error?.message || `HTTP ${res.status}`);
            const list = json.data.versions || [];
            setGenericVersions(list);
            setSelectedGenericVersionId((prev) => {
                if (prev && list.some((v) => v.id === prev)) return prev;
                return list.length ? list[list.length - 1].id : null;
            });
        } catch (err) {
            console.error('Error fetching generic Policy Hub versions:', err);
            setGenericVersions([]);
            setSelectedGenericVersionId(null);
        } finally {
            setLoadingGenericVersions(false);
        }
    };

    const fetchGenericControls = async (fwId, versionIdOverride = undefined) => {
        setLoading(true);
        setError(null);
        try {
            const token = await getToken();
            if (!token) {
                setError('Not authenticated');
                setLoading(false);
                return;
            }
            const effectiveId = versionIdOverride !== undefined ? versionIdOverride : selectedGenericVersionId;
            const q = effectiveId ? `?versionId=${encodeURIComponent(effectiveId)}` : '';
            const res = await fetch(`${API_BASE}/api/v1/frameworks/${fwId}/controls${q}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error?.message || `HTTP ${res.status}`);
            setControls(json.data.controls || []);
            if (json.data.versionId) {
                if (versionIdOverride !== undefined) {
                    setSelectedGenericVersionId(json.data.versionId);
                } else {
                    setSelectedGenericVersionId((prev) => prev ?? json.data.versionId);
                }
            }
        } catch (err) {
            console.error('Error fetching generic controls:', err);
            setError(err.message || 'Failed to load controls');
        } finally {
            setLoading(false);
        }
    };

    const fetchIsoVersions = async () => {
        try {
            setLoadingIsoVersions(true);
            const token = await getToken();
            if (!token) return;
            const res = await fetch(`${API_BASE}/api/v1/iso27001/policy-hub/versions`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error?.message || `HTTP ${res.status}`);
            const list = json.data.versions || [];
            setIsoVersions(list);
            setSelectedIsoVersionId((prev) => {
                if (prev && list.some((v) => v.id === prev)) return prev;
                return list.length ? list[list.length - 1].id : null;
            });
        } catch (err) {
            console.error('Error fetching ISO Policy Hub versions:', err);
            setIsoVersions([]);
            setSelectedIsoVersionId(null);
        } finally {
            setLoadingIsoVersions(false);
        }
    };

    // ── Fetch controls for selected library version (or latest when none selected yet) ──
    /** @param {string | null | undefined} versionIdOverride pass `null` to load latest (e.g. after deleting a snapshot) */
    const fetchControls = async (versionIdOverride = undefined) => {
        setLoading(true);
        setError(null);
        try {
            const token = await getToken();
            if (!token) {
                setError('Not authenticated');
                setLoading(false);
                return;
            }
            const effectiveId = versionIdOverride !== undefined ? versionIdOverride : selectedIsoVersionId;
            const q = effectiveId ? `?versionId=${encodeURIComponent(effectiveId)}` : '';
            const res = await fetch(`${API_BASE}/api/v1/iso27001/controls${q}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error?.message || `HTTP ${res.status}`);
            setControls(json.data.controls || []);
            if (json.data.versionId) {
                if (versionIdOverride !== undefined) {
                    setSelectedIsoVersionId(json.data.versionId);
                } else {
                    setSelectedIsoVersionId((prev) => prev ?? json.data.versionId);
                }
            }
        } catch (err) {
            console.error('Error fetching ISO 27001 controls:', err);
            setError(err.message || 'Failed to load controls');
        } finally {
            setLoading(false);
        }
    };

    const handleFrameworkClick = async (fwId) => {
        if (fwId === 'iso27001') {
            setActiveFramework('iso27001');
            await fetchIsoVersions();
        } else {
            setActiveFramework(fwId);
            await fetchGenericVersions(fwId);
        }
    };

    useEffect(() => {
        if (activeFramework === 'iso27001') {
            fetchControls();
        } else if (activeFramework) {
            fetchGenericControls(activeFramework);
        }
    }, [activeFramework, selectedIsoVersionId, selectedGenericVersionId]);

    const selectedIsoVer = useMemo(
        () => isoVersions.find((v) => v.id === selectedIsoVersionId),
        [isoVersions, selectedIsoVersionId],
    );

    const visibleIsoVersions = useMemo(() => {
        let latest5;
        if (isoVersions.length <= 5) {
            latest5 = [...isoVersions];
        } else {
            latest5 = isoVersions.slice(-5);
            if (!latest5.find(v => v.id === selectedIsoVersionId)) {
                const selected = isoVersions.find(v => v.id === selectedIsoVersionId);
                if (selected) {
                    latest5 = [selected, ...latest5.slice(1)];
                }
            }
        }
        return latest5.reverse();
    }, [isoVersions, selectedIsoVersionId]);

    const olderIsoVersions = useMemo(() => {
        if (isoVersions.length <= 5) return [];
        return isoVersions.filter(v => !visibleIsoVersions.find(vis => vis.id === v.id)).reverse();
    }, [isoVersions, visibleIsoVersions]);

    const selectedGenericVer = useMemo(
        () => genericVersions.find((v) => v.id === selectedGenericVersionId),
        [genericVersions, selectedGenericVersionId],
    );

    const visibleGenericVersions = useMemo(() => {
        let latest5;
        if (genericVersions.length <= 5) {
            latest5 = [...genericVersions];
        } else {
            latest5 = genericVersions.slice(-5);
            if (!latest5.find(v => v.id === selectedGenericVersionId)) {
                const selected = genericVersions.find(v => v.id === selectedGenericVersionId);
                if (selected) {
                    latest5 = [selected, ...latest5.slice(1)];
                }
            }
        }
        return latest5.reverse();
    }, [genericVersions, selectedGenericVersionId]);

    const olderGenericVersions = useMemo(() => {
        if (genericVersions.length <= 5) return [];
        return genericVersions.filter(v => !visibleGenericVersions.find(vis => vis.id === v.id)).reverse();
    }, [genericVersions, visibleGenericVersions]);

    const handleGenericCsvUpload = async (file) => {
        try {
            setGenericUploading(true);
            setGenericUploadResult(null);
            const token = await getToken();
            if (!token) {
                message.error('Not authenticated');
                return false;
            }
            const formData = new FormData();
            formData.append('file', file);
            const res = await fetch(`${API_BASE}/api/v1/frameworks/${activeFramework}/controls/upload-csv`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            const json = await res.json();
            if (!res.ok || !json.success) {
                setGenericUploadResult({ type: 'error', message: json.error?.message || 'Upload failed' });
                message.error(json.error?.message || 'Upload failed');
            } else {
                const v = json.data.version;
                const d = json.data.diffVsPrevious;
                setGenericUploadResult({ type: 'success', ...json.data });
                message.success(
                    v
                        ? `Created version ${v.label}: ${json.data.totalParsed} controls (+${d?.added ?? 0} new / ~${d?.edited ?? 0} edited vs previous)`
                        : 'Upload complete',
                );
                await fetchGenericVersions(activeFramework);
                if (v?.id) setSelectedGenericVersionId(v.id);
            }
        } catch (err) {
            setGenericUploadResult({ type: 'error', message: err.message });
            message.error('Upload failed: ' + err.message);
        } finally {
            setGenericUploading(false);
        }
        return false;
    };

    const handleDeleteGenericVersion = async () => {
        if (!selectedGenericVersionId) return;
        setGenericDeleteLoading(true);
        try {
            const token = await getToken();
            if (!token) {
                message.error('Not authenticated');
                return;
            }
            const res = await fetch(
                `${API_BASE}/api/v1/frameworks/${activeFramework}/versions/${encodeURIComponent(selectedGenericVersionId)}`,
                { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
            );
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) {
                message.error(json.error?.message || 'Failed to delete snapshot');
                return;
            }
            message.success(`Removed snapshot ${json.data?.label || ''}`);
            await fetchGenericVersions(activeFramework);
            await fetchGenericControls(activeFramework, null);
        } catch (e) {
            message.error(e?.message || 'Failed to delete snapshot');
        } finally {
            setGenericDeleteLoading(false);
        }
    };

    const handleIsoCsvUpload = async (file) => {
        try {
            setIsoUploading(true);
            setIsoUploadResult(null);
            const token = await getToken();
            if (!token) {
                message.error('Not authenticated');
                return false;
            }
            const formData = new FormData();
            formData.append('file', file);
            const res = await fetch(`${API_BASE}/api/v1/iso27001/controls/upload-csv`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            const json = await res.json();
            if (!res.ok || !json.success) {
                setIsoUploadResult({ type: 'error', message: json.error?.message || 'Upload failed' });
                message.error(json.error?.message || 'Upload failed');
            } else {
                const v = json.data.version;
                const d = json.data.diffVsPrevious;
                setIsoUploadResult({ type: 'success', ...json.data });
                message.success(
                    v
                        ? `Created version ${v.label}: ${json.data.totalParsed} controls (+${d?.added ?? 0} new / ~${d?.edited ?? 0} edited vs previous)`
                        : 'Upload complete',
                );
                await fetchIsoVersions();
                if (v?.id) setSelectedIsoVersionId(v.id);
                if (fetchTaxonomyPendingCount) fetchTaxonomyPendingCount();
            }
        } catch (err) {
            setIsoUploadResult({ type: 'error', message: err.message });
            message.error('Upload failed: ' + err.message);
        } finally {
            setIsoUploading(false);
        }
        return false;
    };

    const handleDownloadIsoCsv = async () => {
        setIsoDownloadLoading(true);
        try {
            const token = await getToken();
            if (!token) {
                message.error('Not authenticated');
                return;
            }
            const q = selectedIsoVersionId ? `?versionId=${encodeURIComponent(selectedIsoVersionId)}` : '';
            const res = await fetch(`${API_BASE}/api/v1/iso27001/controls/export-csv${q}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                let errMsg = `HTTP ${res.status}`;
                try {
                    const j = await res.json();
                    if (j.error?.message) errMsg = j.error.message;
                } catch {
                    /* ignore */
                }
                message.error(errMsg);
                return;
            }
            const blob = await res.blob();
            const cd = res.headers.get('Content-Disposition');
            let filename = 'iso27001-controls.csv';
            if (cd) {
                const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
                const quoted = /filename="([^"]+)"/i.exec(cd);
                const plain = /filename=([^;\s]+)/i.exec(cd);
                const raw = star?.[1] || quoted?.[1] || plain?.[1];
                if (raw) {
                    try {
                        filename = decodeURIComponent(raw.replace(/^"|"$/g, '').trim());
                    } catch {
                        filename = raw.replace(/^"|"$/g, '').trim();
                    }
                }
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            message.success('CSV downloaded');
        } catch (e) {
            message.error(e?.message || 'Download failed');
        } finally {
            setIsoDownloadLoading(false);
        }
    };

    const latestIsoVersionId = isoVersions.length ? isoVersions[isoVersions.length - 1].id : null;
    const canDeleteSelectedIsoSnapshot =
        isAdmin &&
        selectedIsoVer &&
        selectedIsoVer.minorSeq > 0 &&
        selectedIsoVersionId &&
        selectedIsoVersionId === latestIsoVersionId;

    const handleDeleteIsoVersion = async () => {
        if (!selectedIsoVersionId) return;
        setIsoDeleteLoading(true);
        try {
            const token = await getToken();
            if (!token) {
                message.error('Not authenticated');
                return;
            }
            const res = await fetch(
                `${API_BASE}/api/v1/iso27001/policy-hub/versions/${encodeURIComponent(selectedIsoVersionId)}`,
                { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
            );
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) {
                message.error(json.error?.message || 'Failed to delete snapshot');
                return;
            }
            message.success(`Removed snapshot ${json.data?.label || ''}`);
            await fetchIsoVersions();
            await fetchControls(null);
            if (fetchTaxonomyPendingCount) fetchTaxonomyPendingCount();
        } catch (e) {
            message.error(e?.message || 'Failed to delete snapshot');
        } finally {
            setIsoDeleteLoading(false);
        }
    };

    // ── Derived lists ────────────────────────────────────────────────────
    const themes = useMemo(() => {
        const set = new Set(controls.map((c) => c.controlTheme).filter(Boolean));
        return ['All', ...set];
    }, [controls]);

    const orgFunctions = useMemo(() => {
        const set = new Set();
        controls.forEach((c) => {
            if (c.orgFunction) {
                c.orgFunction.split(',').forEach((fn) => set.add(fn.trim()));
            }
        });
        return ['All', ...Array.from(set).sort()];
    }, [controls]);

    const filteredControls = useMemo(() => {
        const filtered = controls.filter((c) => {
            if (activeFramework === 'iso27001') {
                const matchSearch = !search
                    || c.ctrlNumber?.toLowerCase().includes(search.toLowerCase())
                    || c.ctrlName?.toLowerCase().includes(search.toLowerCase())
                    || c.ctrlDefinition?.toLowerCase().includes(search.toLowerCase());
                const matchTheme = themeFilter === 'All' || c.controlTheme === themeFilter;
                const matchOrg = orgFilter === 'All' || (c.orgFunction && c.orgFunction.includes(orgFilter));
                return matchSearch && matchTheme && matchOrg;
            } else {
                if (!search) return true;
                const q = search.toLowerCase();
                return Object.values(c).some(val => 
                    val && typeof val === 'string' && val.toLowerCase().includes(q)
                );
            }
        });
        
        // Natural order for every framework: 1, 2, 3 … 10 (never text order 1, 10, 2).
        filtered.sort((a, b) => naturalCompare(a.ctrlNumber, b.ctrlNumber));
        return filtered;
    }, [controls, search, themeFilter, orgFilter, activeFramework]);

    // ── Table columns (resizable widths persisted per browser) ────────────
    const controlColumnsBase = useMemo(() => {
        if (activeFramework && activeFramework !== 'iso27001') {
            const cols = [
                {
                    title: 'CONTROL',
                    dataIndex: 'ctrlNumber',
                    key: 'ctrlNumber',
                    width: 132,
                    sorter: sortAnnexControlNumber,
                    render: (_, record) => {
                        return (
                            <Space size={8} align="center" wrap={false} className="policylens-policy-hub-control-cell">
                                <Tag color="blue" style={{ fontWeight: 700 }}>{record.ctrlNumber}</Tag>
                                {record.changeVsBaseline === 'new' && (
                                    <Tag color="green" style={{ fontSize: 10, fontWeight: 700, marginInlineEnd: 0 }}>New</Tag>
                                )}
                                {record.changeVsBaseline === 'edited' && (
                                    <Tag color="blue" style={{ fontSize: 10, fontWeight: 700, marginInlineEnd: 0 }}>Edited</Tag>
                                )}
                            </Space>
                        );
                    },
                },
                {
                    title: 'CONTROL NAME',
                    dataIndex: 'ctrlName',
                    key: 'ctrlName',
                    width: 300,
                    ellipsis: true,
                    sorter: sortByField('ctrlName'),
                    render: (name) => <Text strong style={{ color: 'var(--color-text-hero)' }}>{name}</Text>,
                },
            ];

            // Add dynamic columns based on the first row's customData keys
            if (controls.length > 0) {
                const firstRow = controls[0];
                const customKeys = Object.keys(firstRow).filter(
                    k => !['id', 'versionId', 'frameworkKey', 'ctrlNumber', 'ctrlName', 'createdAt', 'changeVsBaseline', 'changeVsPrevious'].includes(k)
                );
                customKeys.forEach(k => {
                    cols.push({
                        title: k.toUpperCase().replace(/_/g, ' '),
                        dataIndex: k,
                        key: k,
                        width: 200,
                        ellipsis: true,
                        sorter: sortByField(k),
                        render: (val) => <Tooltip title={val}><Text style={{ fontSize: 13 }}>{val || '-'}</Text></Tooltip>,
                    });
                });
            }
            return cols;
        }

        const cols = [
            {
                title: 'CONTROL',
                dataIndex: 'ctrlNumber',
                key: 'ctrlNumber',
                width: 132,
                sorter: sortAnnexControlNumber,
                render: (_, record) => {
                    const rowKey = record.id;
                    const expanded = expandedRowKeys.includes(rowKey);
                    return (
                        <Space size={8} align="center" wrap={false} className="policylens-policy-hub-control-cell">
                            <button
                                type="button"
                                className={`ant-table-row-expand-icon ${expanded ? 'ant-table-row-expand-icon-expanded' : 'ant-table-row-expand-icon-collapsed'}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedRowKeys((prev) =>
                                        (expanded ? prev.filter((k) => k !== rowKey) : [...prev, rowKey])
                                    );
                                }}
                                aria-expanded={expanded}
                                aria-label={expanded ? 'Collapse row' : 'Expand row'}
                            />
                            <Tag color="blue" style={{ fontWeight: 700 }}>{record.ctrlNumber}</Tag>
                            {record.changeVsBaseline === 'new' && (
                                <Tag color="green" style={{ fontSize: 10, fontWeight: 700, marginInlineEnd: 0 }}>New</Tag>
                            )}
                            {record.changeVsBaseline === 'edited' && (
                                <Tag color="blue" style={{ fontSize: 10, fontWeight: 700, marginInlineEnd: 0 }}>Edited</Tag>
                            )}
                        </Space>
                    );
                },
            },
            {
                title: 'CONTROL NAME',
                dataIndex: 'ctrlName',
                key: 'ctrlName',
                width: 300,
                ellipsis: true,
                sorter: sortByField('ctrlName'),
                render: (name) => <Text strong style={{ color: 'var(--color-text-hero)' }}>{name}</Text>,
            },
            {
                title: 'THEME',
                dataIndex: 'controlTheme',
                key: 'controlTheme',
                width: 140,
                sorter: sortByField('controlTheme'),
                render: (theme) =>
                    theme ? (
                        <Tag color={FAMILY_COLORS[theme] || 'default'}>{theme}</Tag>
                    ) : (
                        <Text type="secondary">-</Text>
                    ),
            },
            {
                title: 'ORG FUNCTION',
                dataIndex: 'orgFunction',
                key: 'orgFunction',
                width: 200,
                sorter: sortByField('orgFunction'),
                render: (fn) => <Text type="secondary" style={{ fontSize: 12 }}>{fn}</Text>,
            },
            {
                title: 'KEY POINTS',
                key: 'kpCount',
                width: 100,
                align: 'center',
                sorter: sortKeyPointsCount,
                render: (_, record) => (
                    <Tag color="geekblue">{record.keyPoints?.length || 0}</Tag>
                ),
            },
        ];
        if (isAdmin) {
            cols.push({
                title: 'ACTIONS',
                key: 'actions',
                width: 148,
                align: 'center',
                render: (_, record) => (
                    <Space size={4} onClick={(e) => e.stopPropagation()}>
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => {
                                setEditingControl(record);
                                setControlModalOpen(true);
                            }}
                        >
                            Edit
                        </Button>
                        {record.source === 'admin' ? (
                            <Popconfirm
                                title="Delete this control?"
                                description="Removes it from Policy Hub and deletes its cross-framework row in Control Taxonomy."
                                okText="Delete"
                                okButtonProps={{ danger: true }}
                                cancelText="Cancel"
                                onConfirm={async () => {
                                    const token = await getToken();
                                    if (!token) {
                                        message.error('Not authenticated');
                                        return;
                                    }
                                    try {
                                        const res = await fetch(`${API_BASE}/api/v1/iso27001/controls/${record.id}`, {
                                            method: 'DELETE',
                                            headers: { Authorization: `Bearer ${token}` },
                                        });
                                        const json = await res.json();
                                        if (!res.ok || !json.success) {
                                            throw new Error(json.error?.message || res.statusText);
                                        }
                                        message.success('Control deleted');
                                        setExpandedRowKeys((prev) => prev.filter((k) => k !== record.id));
                                        await fetchControls();
                                        fetchTaxonomyPendingCount();
                                    } catch (err) {
                                        message.error(err.message || 'Delete failed');
                                    }
                                }}
                            >
                                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                                    Delete
                                </Button>
                            </Popconfirm>
                        ) : (
                            <Tooltip title="Standard library controls cannot be deleted">
                                <Button type="link" size="small" disabled icon={<DeleteOutlined />}>
                                    Delete
                                </Button>
                            </Tooltip>
                        )}
                    </Space>
                ),
            });
        }
        return cols;
    }, [expandedRowKeys, isAdmin, activeFramework, controls]);

    useEffect(() => {
        if (!controlModalOpen) return;
        if (editingControl) {
            controlForm.setFieldsValue({
                ctrlNumber: editingControl.ctrlNumber,
                ctrlName: editingControl.ctrlName,
                ctrlDefinition: editingControl.ctrlDefinition,
                ctrlGuidanceText: editingControl.ctrlGuidanceText || '',
                controlTheme: editingControl.controlTheme || undefined,
                orgFunction: editingControl.orgFunction || '',
                keyPointsText: editingControl.keyPoints?.map((k) => k.keyPoint).join('\n') || '',
                preventive: !!editingControl.preventive,
                detective: !!editingControl.detective,
                corrective: !!editingControl.corrective,
                confidentiality: !!editingControl.confidentiality,
                integrity: !!editingControl.integrity,
                availability: !!editingControl.availability,
                coversIntent: !!editingControl.coversIntent,
                coversImplementation: !!editingControl.coversImplementation,
                coversEffectiveness: !!editingControl.coversEffectiveness,
                requeueTaxonomyReview: false,
            });
        } else {
            controlForm.resetFields();
            controlForm.setFieldsValue({
                preventive: false,
                detective: false,
                corrective: false,
                confidentiality: false,
                integrity: false,
                availability: false,
                coversIntent: false,
                coversImplementation: false,
                coversEffectiveness: false,
            });
        }
    }, [controlModalOpen, editingControl, controlForm]);

    const submitControlModal = async () => {
        const values = await controlForm.validateFields();
        const token = await getToken();
        if (!token) {
            message.error('Not authenticated');
            return;
        }
        const keyPoints = values.keyPointsText
            ? values.keyPointsText.split('\n').map((s) => s.trim()).filter(Boolean)
            : [];
        const payload = {
            ctrlNumber: values.ctrlNumber.trim(),
            ctrlName: values.ctrlName.trim(),
            ctrlDefinition: values.ctrlDefinition.trim(),
            ctrlGuidanceText: (values.ctrlGuidanceText || '').trim(),
            controlTheme: values.controlTheme || null,
            orgFunction: (values.orgFunction || '').trim() || null,
            preventive: values.preventive,
            detective: values.detective,
            corrective: values.corrective,
            confidentiality: values.confidentiality,
            integrity: values.integrity,
            availability: values.availability,
            coversIntent: values.coversIntent,
            coversImplementation: values.coversImplementation,
            coversEffectiveness: values.coversEffectiveness,
            keyPoints,
        };
        setSavingControl(true);
        try {
            if (editingControl) {
                const patchBody = { ...payload };
                if (values.requeueTaxonomyReview) {
                    patchBody.requeueTaxonomyReview = true;
                }
                const res = await fetch(`${API_BASE}/api/v1/iso27001/controls/${editingControl.id}`, {
                    method: 'PATCH',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(patchBody),
                });
                const json = await res.json();
                if (!res.ok || !json.success) throw new Error(json.error?.message || res.statusText);
                message.success('Control updated');
            } else {
                const res = await fetch(`${API_BASE}/api/v1/iso27001/controls`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });
                const json = await res.json();
                if (!res.ok || !json.success) throw new Error(json.error?.message || res.statusText);
                message.success('Control created - add cross-framework mapping in Control Taxonomy');
            }
            setControlModalOpen(false);
            setEditingControl(null);
            await fetchControls();
            fetchTaxonomyPendingCount();
        } catch (err) {
            message.error(err.message || 'Save failed');
        } finally {
            setSavingControl(false);
        }
    };

    const { columns: controlColumns, components: policyHubTableComponents } = useResizableTableColumns(
        'policy-hub-iso-controls',
        controlColumnsBase
    );

    // ── Security attribute tags ──────────────────────────────────────────
    const renderSecurityTags = (ctrl) => {
        const attrs = [];
        if (ctrl.preventive) attrs.push({ label: 'Preventive', color: 'green' });
        if (ctrl.detective) attrs.push({ label: 'Detective', color: 'orange' });
        if (ctrl.corrective) attrs.push({ label: 'Corrective', color: 'red' });
        if (ctrl.confidentiality) attrs.push({ label: 'Confidentiality', color: 'blue' });
        if (ctrl.integrity) attrs.push({ label: 'Integrity', color: 'purple' });
        if (ctrl.availability) attrs.push({ label: 'Availability', color: 'cyan' });
        return (
            <Space size={4} wrap>
                {attrs.map((a) => (
                    <Tag key={a.label} color={a.color} style={{ fontSize: 11 }}>{a.label}</Tag>
                ))}
            </Space>
        );
    };

    const renderCoverageTags = (ctrl) => {
        const items = [];
        if (ctrl.coversIntent) items.push('Intent');
        if (ctrl.coversImplementation) items.push('Implementation');
        if (ctrl.coversEffectiveness) items.push('Effectiveness');
        return (
            <Space size={4} wrap>
                {items.map((item) => (
                    <Tag key={item} icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 11 }}>{item}</Tag>
                ))}
            </Space>
        );
    };

    const renderPolicyHubExpandedRow = (ctrl) => {
        const keyPointsSorted = [...(ctrl.keyPoints || [])].sort((a, b) =>
            naturalCompare(a.keyPointId, b.keyPointId)
        );
        return (
            <div className="policylens-policy-hub-expanded">
                <Space size={8} wrap style={{ marginBottom: 12 }}>
                    <Tag color={FAMILY_COLORS[ctrl.controlTheme]} style={{ fontWeight: 700 }}>
                        {ctrl.controlTheme}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>{ctrl.orgFunction}</Text>
                </Space>
                <div style={{ marginBottom: 10 }}>
                    <Text strong style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 6 }}>
                        SECURITY ATTRIBUTES
                    </Text>
                    {renderSecurityTags(ctrl)}
                </div>
                <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 6 }}>
                        COVERAGE
                    </Text>
                    {renderCoverageTags(ctrl)}
                </div>
                <Divider style={{ margin: '12px 0' }} />
                <Row gutter={[20, 20]}>
                    <Col xs={24} lg={12}>
                        <div className="policylens-policy-hub-expanded-panel policylens-policy-hub-expanded-definition">
                            <Text strong className="policylens-policy-hub-expanded-heading">Definition</Text>
                            <div className="policylens-policy-hub-expanded-body">
                                <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 13 }}>
                                    {ctrl.ctrlDefinition || '-'}
                                </Text>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} lg={12}>
                        <div className="policylens-policy-hub-expanded-panel policylens-policy-hub-expanded-keypoints">
                            <Text strong className="policylens-policy-hub-expanded-heading">
                                Key points ({keyPointsSorted.length})
                            </Text>
                            <div className="policylens-policy-hub-expanded-body">
                                {keyPointsSorted.length > 0 ? (
                                    <ul className="policylens-policy-hub-keypoints-list">
                                        {keyPointsSorted.map((kp) => (
                                            <li key={kp.keyPointId || kp.keyPoint}>
                                                <Text style={{ fontSize: 13 }}>{formatKeyPointLine(kp.keyPoint)}</Text>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <Text type="secondary" style={{ fontSize: 13 }}>No key points available.</Text>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    };

    // ── Framework selection grid ──────────────────────────────────────────
    if (!activeFramework) {
        return (
            <PageShell>
                <PageHeader title="Policy Hub" subtitle="Select a regulatory framework to explore authorized standards and controls." />

                <Row gutter={[16, 16]}>
                    {frameworks.map((fw) => {
                        const c = coverage?.[fw.id];
                        const pct = c?.percent;
                        // ISO 22301 / NIST CSF / SOC 2 have no expected total (T): backend reports a loaded count, not a percent.
                        const isCountOnly = c?.kind === 'count';
                        const pctLabel = coverageLoading ? '…' : formatPolicyHubPercent(pct);
                        // The % is cross-mapping coverage; the table below reads the uploaded control
                        // snapshot. Lead with what is actually loaded so the tile never claims a % over an empty table.
                        const loadedN = c?.loadedControls;
                        const hasLoadedCount = typeof loadedN === 'number';
                        const tileTag = coverageLoading
                            ? '…'
                            : !c
                                // Coverage loaded but this key is absent (backend not serving it yet): nothing is loaded.
                                ? (fw.id === 'soc2' && coverage ? 'No controls loaded' : 'Coverage unavailable')
                                : fw.id !== 'iso27001' && hasLoadedCount && loadedN === 0
                                    ? 'No controls loaded'
                                    : isCountOnly || (fw.id !== 'iso27001' && hasLoadedCount)
                                        ? `${loadedN} controls loaded`
                                        : `${pctLabel} complete`;
                        const tagColor = fw.id === 'iso27001'
                            ? 'green'
                            : (!c && fw.id === 'soc2' && coverage)
                              ? 'default'
                              : hasLoadedCount
                                ? (loadedN > 0 ? 'blue' : 'default')
                                : completionPercentTagColor(pct ?? 0);
                        const tooltipTitle = (() => {
                            if (!c) return 'Coverage from Control CrossMapping taxonomy.';
                            if (fw.id === 'iso27001') {
                                return 'Annex A catalog is fully available in Policy Hub.';
                            }
                            if (isCountOnly) {
                                return `No expected control total is defined for this framework, so no completion percentage is shown. ${c.loadedControls} controls are loaded in the latest Policy Hub version.`;
                            }
                            if (typeof c.loadedControls === 'number' && c.loadedControls === 0) {
                                return 'No controls have been uploaded for this framework yet. Open it and upload a CSV to create the first snapshot. The figures below are cross-mapping coverage, not loaded controls.';
                            }
                            return `${c.loadedControls ?? 'Some'} controls are loaded in the latest Policy Hub version. Cross-mapping coverage: Completion % = (U ÷ T) × 100 (capped at 100%). U = unique mapped refs in the crosswalk column; T = framework control total (e.g. PCI DSS 300). Example: 89/300 × 100 ≈ 29.67%. Current: U = ${c.uniqueInColumn}, T = ${c.frameworkTotal}, remaining gap = ${c.gap}. ISO cross-mapping rows = ${c.isoRowCount ?? 'n'}.`;
                        })();
                        return (
                            <Col xs={24} sm={12} lg={8} key={fw.id}>
                                <Card
                                    hoverable
                                    bordered={false}
                                    style={{
                                        cursor: 'pointer',
                                        height: '100%',
                                    }}
                                    onClick={() => handleFrameworkClick(fw.id)}
                                >
                                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                            {FRAMEWORK_ICONS[fw.id]}
                                            <Tooltip title={tooltipTitle}>
                                                <Tag color={tagColor} style={{ fontWeight: 700, fontSize: 11 }}>
                                                    {fw.id === 'iso27001' ? '100% complete' : tileTag}
                                                </Tag>
                                            </Tooltip>
                                        </Space>
                                        <Title level={4} style={{ margin: 0 }}>{fw.name}</Title>
                                        <Text type="secondary" style={{ fontSize: 13 }}>{fw.desc}</Text>
                                        {c && fw.id !== 'iso27001' && !isCountOnly && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                U = {c.uniqueInColumn} · T = {c.frameworkTotal} · Gap (T − U) = {c.gap} · ISO rows = {c.isoRowCount}
                                            </Text>
                                        )}
                                        {fw.id === 'iso27001' && c && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {c.frameworkTotal} Annex A rows in crosswalk
                                            </Text>
                                        )}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid var(--color-border)' }}>
                                            <Text style={{ fontWeight: 700, fontSize: 12 }}>
                                                Explore Matrix
                                            </Text>
                                            <ArrowRightOutlined />
                                        </div>
                                    </Space>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            </PageShell>
        );
    }

    // ── ISO 27001 control registry (layout aligned with Question Bank) ────
    const currentVersions = activeFramework === 'iso27001' ? isoVersions : genericVersions;
    const currentSelectedVersionId = activeFramework === 'iso27001' ? selectedIsoVersionId : selectedGenericVersionId;
    const currentVisibleVersions = activeFramework === 'iso27001' ? visibleIsoVersions : visibleGenericVersions;
    const currentOlderVersions = activeFramework === 'iso27001' ? olderIsoVersions : olderGenericVersions;
    const setCurrentSelectedVersionId = activeFramework === 'iso27001' ? setSelectedIsoVersionId : setSelectedGenericVersionId;
    const currentSelectedVer = activeFramework === 'iso27001' ? selectedIsoVer : selectedGenericVer;
    const currentLoadingVersions = activeFramework === 'iso27001' ? loadingIsoVersions : loadingGenericVersions;

    return (
        <PageShell width="wide" className="policy-hub-iso-view">
            <PageHeader
                title={`${frameworks.find(f => f.id === activeFramework)?.name || 'Framework'} Controls`}
                back={(
                    <Button
                        type="link"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                            setActiveFramework(null);
                            setControls([]);
                            setIsoVersions([]);
                            setSelectedIsoVersionId(null);
                            setGenericVersions([]);
                            setSelectedGenericVersionId(null);
                            setSearch('');
                            setThemeFilter('All');
                            setOrgFilter('All');
                            setExpandedRowKeys([]);
                        }}
                        style={{ paddingLeft: 0 }}
                    >
                        Back to Frameworks
                    </Button>
                )}
                subtitle={(
                    <>
                        {activeFramework === 'iso27001' ? 'Annex A control reference matrix' : 'Control reference matrix'}
                        {currentSelectedVer ? ` - version ${currentSelectedVer.label}` : ''}
                        {' - '}
                        {loading
                            ? '...'
                            : (() => {
                                const total = controls.length;
                                const shown = filteredControls.length;
                                const filtered =
                                    search || themeFilter !== 'All' || orgFilter !== 'All';
                                if (filtered && shown !== total) {
                                    return `${shown} shown (${total} in snapshot)`;
                                }
                                return `${total} controls in this snapshot`;
                            })()}
                    </>
                )}
                actions={(
                <Space wrap size="middle">
                    <Input.Search
                        placeholder="Search library..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 260 }}
                        allowClear
                    />
                    {activeFramework === 'iso27001' && (
                        <Button
                            icon={<DownloadOutlined />}
                            loading={isoDownloadLoading}
                            disabled={!loadingIsoVersions && isoVersions.length === 0}
                            onClick={handleDownloadIsoCsv}
                        >
                            Download CSV
                        </Button>
                    )}
                    {isAdmin && (activeFramework === 'iso27001' ? canDeleteSelectedIsoSnapshot : (selectedGenericVersionId && selectedGenericVersionId === (genericVersions.length ? genericVersions[genericVersions.length - 1].id : null) && genericVersions.length > 1)) && (
                        <Popconfirm
                            title="Delete this version?"
                            description="Removes this snapshot and all its controls. Baseline “1” cannot be deleted."
                            onConfirm={activeFramework === 'iso27001' ? handleDeleteIsoVersion : handleDeleteGenericVersion}
                            okText="Delete"
                            okButtonProps={{ danger: true, loading: activeFramework === 'iso27001' ? isoDeleteLoading : genericDeleteLoading }}
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                        loading={activeFramework === 'iso27001' ? isoDeleteLoading : genericDeleteLoading}
                            >
                                Delete version
                            </Button>
                        </Popconfirm>
                    )}
                    {isAdmin && (
                        <Button
                            icon={<UploadOutlined />}
                            onClick={() => {
                                if (activeFramework === 'iso27001') {
                                    setIsoUploadOpen(true);
                                    setIsoUploadResult(null);
                                } else {
                                    setGenericUploadOpen(true);
                                    setGenericUploadResult(null);
                                }
                            }}
                        >
                            Upload CSV
                        </Button>
                    )}
                </Space>
                )}
            />

            {currentVersions.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Versions</Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <PageFilter
                            value={currentSelectedVersionId}
                            onChange={setCurrentSelectedVersionId}
                            options={currentVisibleVersions.map((v) => ({ label: `v${v.label}`, value: v.id }))}
                        />
                        {currentOlderVersions.length > 0 && (
                            <Select
                                key={currentSelectedVersionId}
                                value={undefined}
                                onChange={setCurrentSelectedVersionId}
                                placeholder={<span><HistoryOutlined style={{ marginRight: 6 }} /> Older versions</span>}
                                options={currentOlderVersions.map((v) => ({ label: `v${v.label}`, value: v.id }))}
                                size="large"
                                style={{ minWidth: 160 }}
                                dropdownMatchSelectWidth={false}
                            />
                        )}
                    </div>
                </div>
            )}

            {currentLoadingVersions && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>Loading library versions…</Text>
            )}

            {currentSelectedVer && (
                <DataCard size="small">
                    <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} title={`Snapshot ${currentSelectedVer.label}`}>
                        <Descriptions.Item label="Uploaded">
                            {currentSelectedVer.uploadedAt ? formatDdMmYyyyTime(currentSelectedVer.uploadedAt) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Total controls">{currentSelectedVer.totalControls}</Descriptions.Item>
                    </Descriptions>
                    {activeFramework === 'iso27001' && (
                        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block', maxWidth: 720 }}>
                            Reference Annex A (ISO/IEC 27001:2022) lists <strong>93</strong> controls in the standard matrix.
                            Your snapshot may show fewer if the CSV omitted rows, or if duplicate <code>ctrl_number</code> values were merged during import (last row wins).
                        </Text>
                    )}
                    {currentSelectedVer.label !== '1' && (
                        <div style={{ marginTop: 12 }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                vs previous snapshot
                            </Text>
                            <Text style={{ fontSize: 13 }}>
                                <Tag color="green">+{currentSelectedVer.vsPreviousAdded} new</Tag>{' '}
                                <Tag color="blue">~{currentSelectedVer.vsPreviousEdited} edited</Tag>
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10, marginBottom: 4 }}>
                                vs version 1 (baseline)
                            </Text>
                            <Text style={{ fontSize: 13 }}>
                                <Tag color="green">+{currentSelectedVer.vsBaselineAdded} new</Tag>{' '}
                                <Tag color="blue">~{currentSelectedVer.vsBaselineEdited} edited</Tag>
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                                Row <strong>New</strong> / <strong>Edited</strong> tags match baseline above (ctrl_name vs version 1), not &quot;vs previous snapshot&quot;.
                            </Text>
                        </div>
                    )}
                </DataCard>
            )}

            {/* Theme / org filters (ISO 27001 only) */}
            {activeFramework === 'iso27001' && (
                <Space style={{ marginBottom: 20 }} size={12} wrap>
                    <Select
                        value={themeFilter}
                        onChange={setThemeFilter}
                        style={{ width: 200 }}
                        size="large"
                        options={themes.map((f) => ({ value: f, label: f === 'All' ? 'All Themes' : f }))}
                    />
                    <Select
                        value={orgFilter}
                        onChange={setOrgFilter}
                        style={{ width: 200 }}
                        size="large"
                        options={orgFunctions.map((f) => ({ value: f, label: f === 'All' ? 'All Functions' : f }))}
                    />
                </Space>
            )}

            {!currentLoadingVersions && currentVersions.length === 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`No ${frameworks.find(f => f.id === activeFramework)?.name || 'framework'} snapshots yet`}
                    description="Upload a CSV to create the first baseline snapshot."
                />
            )}

            {/* Error banner */}
            {error && (
                <DataCard style={{ background: 'var(--color-danger-bg)' }}>
                    <Space>
                        <ExclamationCircleOutlined style={{ color: 'var(--color-danger-fg)' }} />
                        <Text style={{ color: 'var(--color-danger-fg)' }}>Failed to load controls: {error}</Text>
                        <Button size="small" onClick={fetchControls}>Retry</Button>
                    </Space>
                </DataCard>
            )}

            {/* Controls table */}
            <DataCard className="policylens-table-card">
                <div className="policylens-policy-hub-table-xscroll">
                    <Table
                        className="policylens-data-table policylens-policy-hub-table"
                        dataSource={filteredControls}
                        columns={controlColumns}
                        components={policyHubTableComponents}
                        rowKey="id"
                        rowClassName={(record) => {
                            if (record.changeVsBaseline === 'new') return 'policy-hub-iso-row--new';
                            if (record.changeVsBaseline === 'edited') return 'policy-hub-iso-row--edited';
                            return '';
                        }}
                        loading={loading}
                        expandable={activeFramework === 'iso27001' ? {
                            showExpandColumn: false,
                            expandedRowKeys,
                            onExpandedRowsChange: setExpandedRowKeys,
                            expandedRowRender: (record) => renderPolicyHubExpandedRow(record),
                            expandRowByClick: true,
                        } : undefined}
                        pagination={dashboardPagination({
                            pageSize: 15,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} controls`,
                        })}
                        size="middle"
                        tableLayout="fixed"
                        showSorterTooltip={DASHBOARD_SORTER_TOOLTIP}
                        style={{ color: 'var(--color-text-hero)' }}
                    />
                </div>
            </DataCard>

            <Modal
                title={`Upload ${frameworks.find(f => f.id === activeFramework)?.name || 'Framework'} Controls (CSV)`}
                open={genericUploadOpen}
                onCancel={() => setGenericUploadOpen(false)}
                footer={null}
                width={560}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">
                        Each upload creates a <strong>new snapshot</strong> (v1, then v1.1, v1.2, …) with the full matrix.
                        <br /><br />
                        <strong>Required Columns:</strong> <code>ctrl_number</code> and <code>ctrl_name</code>.
                        <br />
                        Any additional columns will be saved dynamically.
                        {genericVersions.length > 0 && (
                            <Text type="warning" style={{ display: 'block', marginTop: 8 }}>
                                <strong>Warning:</strong> Since a baseline version exists, your CSV columns must exactly match the columns from the first version.
                            </Text>
                        )}
                    </Text>
                </div>
                <Dragger
                    accept=".csv"
                    maxCount={1}
                    showUploadList={false}
                    customRequest={({ file }) => handleGenericCsvUpload(file)}
                    disabled={genericUploading}
                    style={{ padding: '20px 0' }}
                >
                    {genericUploading ? (
                        <div>
                            <Spin size="large" />
                            <p style={{ marginTop: 12 }}>Processing CSV…</p>
                        </div>
                    ) : (
                        <div>
                            <p className="ant-upload-drag-icon"><InboxOutlined style={{ fontSize: 48, color: 'var(--color-accent)' }} /></p>
                            <p className="ant-upload-text" style={{ fontWeight: 700 }}>Click or drag CSV file here</p>
                            <p className="ant-upload-hint">Full matrix - CSV, up to 5 MB</p>
                        </div>
                    )}
                </Dragger>
                {genericUploadResult && (
                    <Alert
                        style={{ marginTop: 16 }}
                        type={genericUploadResult.type === 'success' ? 'success' : 'error'}
                        showIcon
                        message={
                            genericUploadResult.type === 'success' && genericUploadResult.version
                                ? `Version ${genericUploadResult.version.label} - ${genericUploadResult.totalParsed} controls (+${genericUploadResult.diffVsPrevious?.added ?? 0} new / ~${genericUploadResult.diffVsPrevious?.edited ?? 0} edited)`
                                : genericUploadResult.message || 'Upload failed'
                        }
                    />
                )}
            </Modal>

            <Modal
                title="Upload ISO 27001 Controls (CSV)"
                open={isoUploadOpen}
                onCancel={() => setIsoUploadOpen(false)}
                footer={null}
                width={560}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">
                        Each upload creates a <strong>new snapshot</strong> (version 1, then 1.1, 1.2, …) with the full matrix.
                        New or changed controls vs the previous snapshot are flagged for <strong>Control Taxonomy</strong> review (same idea as editing a control in Policy Hub).
                        Use the same column layout as <code>ISO27001_Controls.csv</code> (ctrl_number, ctrl_name, ctrl_definition, …).
                    </Text>
                </div>
                <Dragger
                    accept=".csv"
                    maxCount={1}
                    showUploadList={false}
                    customRequest={({ file }) => handleIsoCsvUpload(file)}
                    disabled={isoUploading}
                    style={{ padding: '20px 0' }}
                >
                    {isoUploading ? (
                        <div>
                            <Spin size="large" />
                            <p style={{ marginTop: 12 }}>Processing CSV…</p>
                        </div>
                    ) : (
                        <div>
                            <p className="ant-upload-drag-icon"><InboxOutlined style={{ fontSize: 48, color: 'var(--color-accent)' }} /></p>
                            <p className="ant-upload-text" style={{ fontWeight: 700 }}>Click or drag CSV file here</p>
                            <p className="ant-upload-hint">Full Annex A matrix - CSV, up to 25 MB</p>
                        </div>
                    )}
                </Dragger>
                {isoUploadResult && (
                    <Alert
                        style={{ marginTop: 16 }}
                        type={isoUploadResult.type === 'success' ? 'success' : 'error'}
                        showIcon
                        message={
                            isoUploadResult.type === 'success' && isoUploadResult.version
                                ? `Version ${isoUploadResult.version.label} - ${isoUploadResult.totalParsed} controls (+${isoUploadResult.diffVsPrevious?.added ?? 0} new / ~${isoUploadResult.diffVsPrevious?.edited ?? 0} edited)`
                                : isoUploadResult.message || 'Upload failed'
                        }
                    />
                )}
            </Modal>

            <Modal
                title={editingControl ? `Edit control ${editingControl.ctrlNumber}` : 'Add ISO 27001 control'}
                open={controlModalOpen}
                onCancel={() => {
                    setControlModalOpen(false);
                    setEditingControl(null);
                }}
                onOk={submitControlModal}
                confirmLoading={savingControl}
                width={720}
                destroyOnClose
            >
                <Form form={controlForm} layout="vertical" style={{ marginTop: 8 }}>
                    <Form.Item name="ctrlNumber" label="Control number" rules={[{ required: true, message: 'Required' }]}>
                        <Input placeholder="e.g. 5.16" disabled={!!editingControl} />
                    </Form.Item>
                    <Form.Item name="ctrlName" label="Control name" rules={[{ required: true, message: 'Required' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="ctrlDefinition" label="Definition" rules={[{ required: true, message: 'Required' }]}>
                        <Input.TextArea rows={4} />
                    </Form.Item>
                    <Form.Item name="ctrlGuidanceText" label="Guidance (optional)">
                        <Input.TextArea rows={3} placeholder="Defaults to definition if empty" />
                    </Form.Item>
                    <Form.Item name="controlTheme" label="Theme">
                        <Select
                            allowClear
                            placeholder="Theme"
                            options={['Organizational', 'People', 'Physical', 'Technological'].map((t) => ({ value: t, label: t }))}
                        />
                    </Form.Item>
                    <Form.Item name="orgFunction" label="Org function">
                        <Input placeholder="Comma-separated" />
                    </Form.Item>
                    <Form.Item name="keyPointsText" label="Key points (one per line)">
                        <Input.TextArea rows={5} placeholder="One bullet per line" />
                    </Form.Item>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>Security attributes</Text>
                    <Space wrap>
                        <Form.Item name="preventive" label="Preventive" valuePropName="checked" style={{ marginBottom: 0 }}><Switch /></Form.Item>
                        <Form.Item name="detective" label="Detective" valuePropName="checked" style={{ marginBottom: 0 }}><Switch /></Form.Item>
                        <Form.Item name="corrective" label="Corrective" valuePropName="checked" style={{ marginBottom: 0 }}><Switch /></Form.Item>
                        <Form.Item name="confidentiality" label="Confidentiality" valuePropName="checked" style={{ marginBottom: 0 }}><Switch /></Form.Item>
                        <Form.Item name="integrity" label="Integrity" valuePropName="checked" style={{ marginBottom: 0 }}><Switch /></Form.Item>
                        <Form.Item name="availability" label="Availability" valuePropName="checked" style={{ marginBottom: 0 }}><Switch /></Form.Item>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', margin: '12px 0 8px' }}>Coverage</Text>
                    <Space wrap>
                        <Form.Item name="coversIntent" label="Intent" valuePropName="checked" style={{ marginBottom: 0 }}><Switch /></Form.Item>
                        <Form.Item name="coversImplementation" label="Implementation" valuePropName="checked" style={{ marginBottom: 0 }}><Switch /></Form.Item>
                        <Form.Item name="coversEffectiveness" label="Effectiveness" valuePropName="checked" style={{ marginBottom: 0 }}><Switch /></Form.Item>
                    </Space>
                    {editingControl && (
                        <Form.Item name="requeueTaxonomyReview" label="Send back to Control Taxonomy review" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </PageShell>
    );
};

export default PolicyLibrary;
